import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { funnelsApi, type FunnelDetailsResponse } from '../../lib/api.js';
import { queryKeys } from '../../lib/query-keys.js';
import { BasicInfoFieldAnalysis } from './BasicInfoFieldAnalysis.js';
import { ANALYSIS_TYPE_LABELS } from './constants.js';
import { GenericDetailsTable } from './GenericDetailsTable.js';
import { PurchaseBuyTypeBreakdown } from './PurchaseBuyTypeBreakdown.js';
import { PurchaseSourceFunnel } from './PurchaseSourceFunnel.js';

/** @graph-connects none */
function DeepDiveContent({
  funnelId,
  analysisType,
  data,
}: {
  funnelId: string;
  analysisType: string;
  data: FunnelDetailsResponse;
}) {
  if (funnelId === 'purchase' && analysisType === 'source_funnel') {
    return <PurchaseSourceFunnel data={data.data} />;
  }
  if (funnelId === 'purchase' && analysisType === 'buy_type_breakdown') {
    return <PurchaseBuyTypeBreakdown data={data.data} />;
  }
  if (funnelId === 'registration' && analysisType === 'basic_info_fields') {
    return <BasicInfoFieldAnalysis data={data.data} />;
  }

  return <GenericDetailsTable data={data.data} />;
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネル詳細ページの深掘り分析タブ。funnelDef.analysisTypes から選択ボタンを並べ、選択された analysisType に応じて専用コンポーネント (PurchaseSourceFunnel / PurchaseBuyTypeBreakdown / BasicInfoFieldAnalysis) または GenericDetailsTable を描画する。
 * @graph-connects funnelsApi [calls] funnels/details API
 * @graph-connects PurchaseSourceFunnel [calls] 購入ソース別
 * @graph-connects PurchaseBuyTypeBreakdown [calls] 購入タイプ別
 * @graph-connects BasicInfoFieldAnalysis [calls] 基本情報フィールド入力率
 * @graph-connects GenericDetailsTable [calls] 汎用テーブル fallback
 */
export function DeepDiveTab({
  funnelId,
  funnelDef,
  startDate,
  endDate,
}: {
  funnelId: string;
  funnelDef: { analysisTypes: string[] } | undefined;
  startDate: string;
  endDate: string;
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'ja';
  const analysisTypes = funnelDef?.analysisTypes ?? [];
  const [selectedType, setSelectedType] = useState(analysisTypes[0] ?? '');

  const detailsQuery = useQuery({
    queryKey: queryKeys.funnels.details(funnelId, startDate, endDate, selectedType),
    queryFn: () => funnelsApi.getDetails(funnelId, startDate, endDate, selectedType),
    staleTime: 5 * 60 * 1000,
    enabled: selectedType !== '',
  });

  if (analysisTypes.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          深掘り分析は現在利用できません
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {analysisTypes.map((type) => (
          <button
            key={type}
            onClick={() => {
              setSelectedType(type);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors border ${
              selectedType === type
                ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-surface)] text-[var(--color-accent-primary)]'
                : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent-primary)]'
            }`}
          >
            {ANALYSIS_TYPE_LABELS[type][lang]}
          </button>
        ))}
      </div>

      {detailsQuery.isLoading ? (
        <div className="card">
          <div className="skeleton-shimmer h-64 w-full rounded-lg" />
        </div>
      ) : detailsQuery.data ? (
        <DeepDiveContent funnelId={funnelId} analysisType={selectedType} data={detailsQuery.data} />
      ) : null}
    </div>
  );
}
